import { NodeViewWrapper, type NodeViewProps } from "@tiptap/react";
import { TagComponent } from "@pineseed/gui/component/tag";
import { useTags } from "../hooks/useTags";
import type { Tag } from "@pineseed/gui/types";

/**
 * Tiptap Mention Node View
 * STRICT ARCHITECTURE COMPLIANCE: Pure UI component only
 * - No business logic
 * - Only renders Tag component within Tiptap editor
 * - Uses existing tag system
 * - Resilient to race conditions with fallback tag creation
 */
export function TiptapMentionNodeView({ node }: NodeViewProps) {
  const { activeWorkspaceTags } = useTags();

  // Find the tag by ID from the mention attributes
  let tag = activeWorkspaceTags.find((t: Tag) => t.id === node.attrs.id);

  // If tag not found in state (race condition), create a fallback tag object
  // This handles the case where the tag was just created but hasn't synced to state yet
  if (!tag && node.attrs.id && node.attrs.label) {
    tag = {
      id: node.attrs.id,
      name: node.attrs.label,
      type: "general" as const,
      color: "#6366f1", // Default color
      icon: "tag",
      workspaceId: "", // Will be set when state syncs
      createdAt: new Date(),
      updatedAt: new Date(),
    } as Tag;
  }

  // If still no tag data available, render as plain text (final fallback)
  if (!tag) {
    return (
      <NodeViewWrapper className="inline">
        <span className="text-muted-foreground">@{node.attrs.label}</span>
      </NodeViewWrapper>
    );
  }

  // Render the tag component
  return (
    <NodeViewWrapper className="inline">
      <TagComponent
        tag={tag}
        size="sm"
        variant="default"
        interactive={false}
        className="mx-0.5"
      />
    </NodeViewWrapper>
  );
}
